import { useState, useEffect } from "react";
import {
  Container, Header, Tabs, Table, Cards, Button, SpaceBetween, Box,
  Badge, Alert, Modal, FormField, StatusIndicator, Select, ColumnLayout,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";
import { useAuth } from "../../context/AuthContext";

export default function SecurityMonitoring() {
  const { adminUser } = useAuth();
  const isSuper = adminUser?.admin_level === "super";
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [suspicious, setSuspicious] = useState(null);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [summaryDays, setSummaryDays] = useState({ label: "7 days", value: 7 });
  const [alerts, setAlerts] = useState([]);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");

  const daysOptions = [
    { label: "1 day", value: 1 }, { label: "7 days", value: 7 },
    { label: "30 days", value: 30 }, { label: "90 days", value: 90 },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, sess, sus] = await Promise.all([
        adminService.getSecuritySummary(summaryDays.value),
        adminService.getAuditLogs({ limit: 50 }),
        adminService.getActiveSessions(),
        adminService.getSuspiciousActivity(),
      ]);
      setSummary(s);
      setAuditLogs(l);
      setSessions(sess);
      setSuspicious(sus);
    } catch (e) {
      setAlerts([{ id: "err", type: "error", content: "Failed to load security data." }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [summaryDays.value]);

  const handleRevoke = async () => {
    try {
      await adminService.revokeAccess(revokeTarget.admin_user_id, revokeReason);
      setAlerts([{ id: "ok", type: "success", content: `Access revoked for ${revokeTarget.email}` }]);
      setRevokeTarget(null);
      setRevokeReason("");
      load();
    } catch {
      setAlerts([{ id: "err", type: "error", content: "Failed to revoke access." }]);
    }
  };

  const overviewContent = (
    <SpaceBetween size="l">
      {alerts.map(a => <Alert key={a.id} type={a.type} dismissible onDismiss={() => setAlerts(al => al.filter(x => x.id !== a.id))}>{a.content}</Alert>)}

      <Container header={<Header>Security Summary</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={2}>
            <FormField label="Summary Period">
              <Select selectedOption={summaryDays} onChange={({ detail }) => setSummaryDays(detail.selectedOption)} options={daysOptions} />
            </FormField>
          </ColumnLayout>

          {summary && (
            <Cards
              cardDefinition={{
                header: i => i.title,
                sections: [
                  { id: "val", content: i => <Box fontSize="heading-xl" fontWeight="bold" color={i.color}>{i.value}</Box> },
                  { id: "desc", content: i => i.description },
                ],
              }}
              cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }, { minWidth: 800, cards: 4 }]}
              loading={loading}
              items={[
                { title: "Total Actions", value: summary.total_actions, description: `Admin actions in last ${summaryDays.label}`, color: "text-status-info" },
                { title: "Admin Logins", value: summary.admin_logins, description: "Successful admin logins", color: "text-status-success" },
                { title: "Access Revocations", value: summary.access_revocations, description: "Admin privileges revoked", color: "text-status-warning" },
                { title: "Unauthorized Attempts", value: summary.unauthorized_attempts, description: "Failed/unauthorized attempts", color: "text-status-error" },
              ]}
            />
          )}
        </SpaceBetween>
      </Container>

      {suspicious?.alerts?.length > 0 && (
        <Container header={<Header>Security Alerts</Header>}>
          <Table
            columnDefinitions={[
              { id: "type", header: "Alert Type", cell: i => i.type.replace(/_/g, " ").toUpperCase() },
              { id: "severity", header: "Severity", cell: i => <StatusIndicator type={i.severity === "high" ? "error" : "warning"}>{i.severity}</StatusIndicator> },
              { id: "desc", header: "Description", cell: i => i.description },
              { id: "count", header: "Count", cell: i => i.count || "—" },
            ]}
            items={suspicious.alerts}
            loading={loading}
            empty="No security alerts detected"
          />
        </Container>
      )}
    </SpaceBetween>
  );

  const sessionsContent = (
    <Container header={<Header counter={`(${sessions.length})`} actions={<Button onClick={load} loading={loading}>Refresh</Button>}>Active Admin Sessions</Header>}>
      <Table
        columnDefinitions={[
          { id: "email", header: "Admin User", cell: i => i.email },
          { id: "level", header: "Level", cell: i => <Badge color={i.admin_level === "super" ? "red" : "blue"}>{i.admin_level}</Badge> },
          { id: "last", header: "Last Activity", cell: i => i.last_verified ? new Date(i.last_verified).toLocaleString() : "—" },
          {
            id: "actions", header: "Actions",
            cell: i => {
              const cantRevoke = i.admin_user_id === adminUser?.admin_user_id || (!isSuper && i.admin_level === "super");
              return (
                <Button size="small" disabled={cantRevoke}
                  onClick={() => { setRevokeTarget(i); setRevokeReason(""); }}>
                  Revoke Access
                </Button>
              );
            },
          },
        ]}
        items={sessions}
        loading={loading}
        empty="No active admin sessions"
      />
    </Container>
  );

  const auditContent = (
    <Container header={<Header counter={`(${auditLogs.length})`} actions={<Button onClick={load} loading={loading}>Refresh</Button>}>Audit Logs</Header>}>
      <Table
        columnDefinitions={[
          { id: "ts", header: "Timestamp", cell: i => i.timestamp ? new Date(i.timestamp).toLocaleString() : "—" },
          { id: "action", header: "Action", cell: i => i.action },
          { id: "admin", header: "Admin", cell: i => i.admin_user_id || "System" },
          { id: "sev", header: "Severity", cell: i => <Badge color={i.severity === "error" ? "red" : i.severity === "warning" ? "yellow" : "blue"}>{i.severity || "info"}</Badge> },
          { id: "details", header: "Details", cell: i => i.details ? JSON.stringify(i.details).slice(0, 80) : "—" },
        ]}
        items={auditLogs}
        loading={loading}
        empty="No audit logs found"
      />
    </Container>
  );

  return (
    <SpaceBetween size="l">
      <Tabs
        activeTabId={selectedTab}
        onChange={({ detail }) => setSelectedTab(detail.activeTabId)}
        tabs={[
          { id: "overview", label: "Overview", content: overviewContent },
          { id: "sessions", label: "Active Sessions", content: sessionsContent },
          { id: "audit", label: "Audit Logs", content: auditContent },
        ]}
      />

      <Modal
        visible={!!revokeTarget}
        onDismiss={() => setRevokeTarget(null)}
        header="Revoke Admin Access"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button variant="primary" onClick={handleRevoke}>Revoke Access</Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning">This will immediately revoke admin access for {revokeTarget?.email}. This cannot be undone.</Alert>
          <FormField label="Reason (optional)">
            <textarea
              value={revokeReason}
              onChange={e => setRevokeReason(e.target.value)}
              rows={3}
              style={{ width: "100%", padding: 8 }}
              placeholder="Enter reason..."
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
